import type { FlightSchoolCourseStudent } from '@/types/FlightSchoolStudent';
import { TestType } from './TestReview';

export enum QuizType {
    'eoc' = 'EOC',
    'practice' = 'Practice',
    'stage' = 'Stage',
    'suFinal' = 'Final',
    'suOptional' = 'Optional',
    'studyQuiz' = 'Study',
}

export enum QuizStatus {
    inProgress = 'in progress',
    notStarted = 'not started',
    complete = 'completed',
}

export type QuizPerformance = {
    status: QuizStatus;
    startedDate?: Date;
    completedDate?: Date;
    score?: number;
    testSessionId?: number;
};

export type FAAKnowledgeTest = {
    completedDate: Date;
    score: number;
};

export type SubunitQuiz = QuizPerformance & { subUnit: number, type: TestType.Study };
export type FinalQuiz = QuizPerformance & { type: TestType.Final };
export type OptionalQuiz = QuizPerformance & { type: TestType.Optional };
export type StagePerformance = QuizPerformance & { stageNumber: number, type: TestType.Stage };
export type EocPerformance = QuizPerformance & { type: TestType.Eoc };
export type PracticePerformance = QuizPerformance & { number: number, type: TestType.Practice };

export type StudyUnitPerformance = {
    studyUnitNumber: number;
    stageNumber: number;
    finalQuizPerformance: FinalQuiz;
    optionalQuizPerformance: OptionalQuiz;
};

export type StudentRatingPerformance = {
    student: FlightSchoolCourseStudent;
    stagePerformance: StagePerformance[];
    studyUnitPerformance: StudyUnitPerformance[];
    eocPerformance: EocPerformance;
    practicePerformance: PracticePerformance[];
    averageScore: number|null;
};

function isQuizPerformance(quizPerformance: unknown): quizPerformance is QuizPerformance {
    return (quizPerformance as QuizPerformance).status !== undefined;
}

export function quizPerformanceToGradeString(quizPerformance: QuizPerformance|string): string {
    if (!isQuizPerformance(quizPerformance)) {
        return '--';
    }

    if (quizPerformance.status === QuizStatus.inProgress) {
        return 'IP';
    }

    return quizPerformance.score?.toString() ?? '--';
}

export type StudyUnitStudyQuizPerformance = QuizPerformance & {
    studyUnitNumber: number;
    uuid: string;
    type: TestType.Study;
};

export type ExamPerformanceTypes = StudyUnitStudyQuizPerformance
    | StudyUnitPerformance
    | StagePerformance
    | EocPerformance
    | PracticePerformance
    | null;

export type StudyUnitTime = {
    studyUnitNumber: number;
    outlineTime: number;
    studyQuestionsTime: number;
    finalQuizTime: number;
    optionalQuizTime: number;
};

export type StageTestTime = {
    stageNumber: number;
    stageTime: number;
};

export type PracticeTime = {
    practiceNumber: number;
    practiceTime: number;
};

export interface StudentRatingTiming {
    studyUnitTimes: StudyUnitTime[];
    stageTimes: StageTestTime[];
    eocTime: number;
    practiceTimes: PracticeTime[];
    totalTime: number;
    requiredTime: number;

    getElementTime(elementType: TestType, elementNumber: number): number;
    getStudyUnitTime(studyUnitNumber: number): StudyUnitTime|undefined;
    getStudyUnitTotalTime(studyUnitNumber: number): number;
    getStageTestTime(stageNumber: number): number;
    getPracticeTime(practiceNumber: number): number;
}

export class StudentRatingTimeInformation implements StudentRatingTiming {
    studyUnitTimes: StudyUnitTime[];

    stageTimes: StageTestTime[];

    eocTime: number;

    practiceTimes: PracticeTime[];

    totalTime: number;

    requiredTime: number;

    public constructor(
        studyUnitTimes: StudyUnitTime[],
        stageTimes: StageTestTime[],
        eocTime: number,
        practiceTimes: PracticeTime[],
        totalTime: number,
        requiredTime: number,
    ) {
        this.studyUnitTimes = studyUnitTimes;
        this.stageTimes = stageTimes;
        this.eocTime = eocTime;
        this.practiceTimes = practiceTimes;
        this.totalTime = totalTime;
        this.requiredTime = requiredTime;
    }

    getElementTime(elementType: TestType, elementNumber: number): number {
        switch (elementType) {
        case TestType.Study:
            return this.getStudyUnitTotalTime(elementNumber);
        case TestType.Stage:
            return this.getStageTestTime(elementNumber);
        case TestType.Eoc:
            return this.eocTime;
        case TestType.Practice:
            return this.getPracticeTime(elementNumber);
        case TestType.Final:
            return this.getStudyUnitTime(elementNumber)?.finalQuizTime ?? 0;
        case TestType.Optional:
            return this.getStudyUnitTime(elementNumber)?.optionalQuizTime ?? 0;
        default:
            return 0;
        }
    }

    getStudyUnitTime(studyUnitNumber: number): StudyUnitTime|undefined {
        return this.studyUnitTimes.find((suTime) => suTime.studyUnitNumber === studyUnitNumber);
    }

    getStudyUnitTotalTime(studyUnitNumber: number): number {
        const suTime = this.getStudyUnitTime(studyUnitNumber);
        return suTime !== undefined
            ? suTime.outlineTime
                + suTime.studyQuestionsTime
                + suTime.finalQuizTime
                + suTime.optionalQuizTime
            : 0;
    }

    getStageTestTime(stageNumber: number): number {
        const stageTime = this.stageTimes.find((stage) => stage.stageNumber === stageNumber);

        return stageTime !== undefined ? stageTime.stageTime : 0;
    }

    getPracticeTime(practiceNumber: number): number {
        const practiceTime = this.practiceTimes.find(
            (practice) => practice.practiceNumber === practiceNumber,
        );

        return practiceTime !== undefined ? practiceTime.practiceTime : 0;
    }
}

export function capElementTimes(
    times: StudentRatingTimeInformation,
    maxElementTime: number,
): StudentRatingTimeInformation {
    const studyUnitTimes = times.studyUnitTimes.map((studyUnitTime) => ({
        studyUnitNumber: studyUnitTime.studyUnitNumber,
        outlineTime: Math.min(studyUnitTime.outlineTime, maxElementTime),
        studyQuestionsTime: Math.min(studyUnitTime.studyQuestionsTime, maxElementTime),
        finalQuizTime: Math.min(studyUnitTime.finalQuizTime, maxElementTime),
        optionalQuizTime: Math.min(studyUnitTime.optionalQuizTime, maxElementTime),
    }));

    const stageTimes = times.stageTimes.map((stageTime) => ({
        stageNumber: stageTime.stageNumber,
        stageTime: Math.min(stageTime.stageTime, maxElementTime),
    }));

    const eocTime = Math.min(times.eocTime, maxElementTime);

    const practiceTimes = times.practiceTimes.map((practiceTime) => ({
        practiceNumber: practiceTime.practiceNumber,
        practiceTime: Math.min(practiceTime.practiceTime, maxElementTime),
    }));

    return new StudentRatingTimeInformation(
        studyUnitTimes,
        stageTimes,
        eocTime,
        practiceTimes,
        times.totalTime,
        times.requiredTime,
    );
}

export function getDisplayTime(secondsTime: number): string {
    const sign = secondsTime >= 0 ? '' : '-';
    const absTime = Math.abs(secondsTime);

    const hours = Math.floor(absTime / 3600);
    const minutes = Math.floor((absTime % 3600) / 60);
    const seconds = Math.round(absTime % 60);
    const padDigits = (checkNum: number): string => (checkNum < 10 ? `0${checkNum}` : checkNum.toString());

    return [`${sign}${padDigits(hours)}`, padDigits(minutes), padDigits(seconds)].join(':');
}

export type PersonalInformationRow = {
    unitLabel: string;
    outlineTime: string;
    studyQuestionsTime: string;
    studyQuestionsScore: StudyUnitStudyQuizPerformance|string;
    finalQuizTime: string;
    finalQuizScore: FinalQuiz|StagePerformance|EocPerformance|PracticePerformance|string;
    optionalQuizTime: string;
    optionalQuizScore: OptionalQuiz|string;
    dateCompleted: string;
    totalTime: string;
};
